import React, { useMemo, forwardRef, ReactNode, HTMLAttributeAnchorTarget, ComponentType } from 'react';

import type { RouterProps } from '../../../internal/types/routerTypes';
import { HStack, VStack } from '../../Stack';
import { SidebarNotification } from '../SidebarNotification';
import { ColorlessIconProps, IconArrowRight, IconBeta } from '../../../icons';
import { useInternalVerticalAlign } from '../../../hooks/useVerticalAlign/useVerticalAlign';
import { SidebarPopup } from '../SidebarPopup';
import { TestProps } from '../../../internal/types/testTypes';
import { ListItem } from '../../List';

export type SidebarItemMobileProps = TestProps &
    RouterProps & {
        /**
         * Элемент, описывающий вложенный уровень пунктов, открывающийся по клику на триггер
         */
        children?: ReactNode | ((closePopup: VoidFunction) => ReactNode);
        /**
         * Текст пункта сайдбара
         */
        title: ReactNode;
        /**
         * Иконка для свёрнутого состояния
         */
        icon?: ComponentType<ColorlessIconProps>;
        /**
         * Заголовок для Drawer в мобильной версии компонента
         */
        label?: string;
        /**
         * Идентификатор пункта меню
         */
        itemId: string;
        /**
         * Дополнительный класс для элемента сайдбара
         */
        className?: string;
        /**
         * Признак отключенного пункта.
         * У отключенного пункта серый цвет текста и иконки, отключен ховер и не происходит действие на клик
         */
        isDisabled?: boolean;
        /**
         * Указатель, что пункт сайдбара относится к бета-версии раздела
         */
        isBeta?: boolean;
        /**
         * Ссылка пункта меню – если есть переход на новый URL
         */
        href?: string;
        /**
         * target пункта меню
         */
        target?: HTMLAttributeAnchorTarget;
        /**
         * Наличие уведомления на пункте меню
         */
        hasNotification?: boolean;
        /**
         * Количество уведомлений на пункте меню, показывается в случае переданного hasNotification={true}
         */
        notificationsCount?: number;
    };

/**
 * Sidebar Item для мобильной версии
 */
export const SidebarItemMobile = forwardRef<HTMLElement, SidebarItemMobileProps>(
    function SidebarItemMobile(props, ref) {
        const {
            'data-testid': dataTestId,
            children,
            className,
            hasNotification,
            href,
            icon: Icon,
            isBeta,
            isDisabled,
            itemId,
            label,
            notificationsCount,
            target,
            routerOptions,
            title,
        } = props;
        const { addonClasses: alignAddonClass } = useInternalVerticalAlign({
            textSize: '14-20',
            addonSize: '16',
        });

        const titleElement = useMemo(() => {
            const titleElement = hasNotification ? (
                <SidebarNotification counter={notificationsCount}>{title}</SidebarNotification>
            ) : (
                <>{title}</>
            );

            return (
                <HStack ref={ref} data-testid={dataTestId ? `Sidebar.${dataTestId}` : 'Sidebar.SidebarItem'}>
                    {titleElement}
                </HStack>
            );
        }, [hasNotification, notificationsCount, title, ref, dataTestId]);

        const item = useMemo(() => {
            const hasArrowRight = Boolean(children);
            const isLink = !isDisabled && Boolean(href && !children);

            const item = (
                <ListItem
                    className={className}
                    // DIRECT-337096: элемент исключён из поиска, при доработке заменить на текст для поиска
                    textValue={null}
                    iconLeft={Icon}
                    iconRight={hasArrowRight ? IconArrowRight : undefined}
                    target={target ?? '_self'}
                    href={isLink ? href : undefined}
                    routerOptions={routerOptions}
                    isDisabled={isDisabled}
                    logNode={`Sidebar.MenuItem.${itemId}`}
                >
                    <HStack gap="current-help">
                        {titleElement}
                        {isBeta && (
                            <VStack className={alignAddonClass}>
                                <IconBeta size="16" />
                            </VStack>
                        )}
                    </HStack>
                </ListItem>
            );

            return item;
        }, [
            children,
            isDisabled,
            href,
            routerOptions,
            className,
            Icon,
            target,
            itemId,
            titleElement,
            isBeta,
            alignAddonClass,
        ]);

        const popupTitle = label || (typeof title === 'string' ? title : undefined);

        if (!children) {
            return item;
        }

        return (
            <SidebarPopup
                data-testid={`SidebarPopup.${itemId}`}
                id={itemId}
                content={children}
                title={popupTitle}
                isDisabled={isDisabled}
            >
                {item}
            </SidebarPopup>
        );
    },
);
